import {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import toast from 'react-hot-toast';
import {MCPOAuthApi} from '../../api/authority/generated/index.ts';
import Button from '../../components/Button.jsx';
import {GlobalContext} from '../../utils/context/GlobalContextProvider.jsx';

export const loadAccountMcpGrants = (api) => api.listAccountMCPGrants();

export async function loadAccountMcpConnections(api) {
    const [metadata, grants] = await Promise.all([
        api.mCPProtectedResourceMetadata(),
        loadAccountMcpGrants(api),
    ]);
    return {resource: metadata.resource, grants};
}

export async function revokeAndReloadAccountMcpGrant(api, grantId) {
    await api.revokeAccountMCPGrant({grantId});
    return loadAccountMcpGrants(api);
}

export function AccountMcpGrantList({grants, confirmingGrantId, revokingGrantId, onStartRevoke, onCancelRevoke, onConfirmRevoke, t}) {
    const confirmButtonRef = useRef(null);
    const revokeButtonRefs = useRef(new Map());
    const returnFocusGrantIdRef = useRef(null);
    useEffect(() => {
        if (confirmingGrantId !== null) confirmButtonRef.current?.focus();
        else if (returnFocusGrantIdRef.current !== null) {
            revokeButtonRefs.current.get(returnFocusGrantIdRef.current)?.focus();
            returnFocusGrantIdRef.current = null;
        }
    }, [confirmingGrantId]);

    if (grants.length === 0) return <p className="text-sm text-slate-500 dark:text-slate-400">{t('user.mcp.empty', {defaultValue: 'K vašemu účtu není připojen žádný AI klient.'})}</p>;

    return <div className="space-y-3">{grants.map((grant) => <article key={grant.grantId} className="app-surface rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="font-medium text-slate-900 dark:text-slate-100">{grant.clientName}</h3>
                <p className="mt-1 break-all font-mono text-xs text-slate-500 dark:text-slate-400">{grant.resource}</p>
            </div>
            {confirmingGrantId !== grant.grantId && <Button ref={(node) => {
                if (node) revokeButtonRefs.current.set(grant.grantId, node);
                else revokeButtonRefs.current.delete(grant.grantId);
            }} variant="outline-red" size="small" disabled={revokingGrantId !== null} aria-label={t('user.mcp.revoke-client', {clientName: grant.clientName, defaultValue: 'Odpojit klienta {{clientName}}'})} onClick={() => {
                returnFocusGrantIdRef.current = grant.grantId;
                onStartRevoke(grant.grantId);
            }}>{t('user.mcp.revoke', {defaultValue: 'Odpojit'})}</Button>}
        </div>
        {confirmingGrantId === grant.grantId && <div role="group" aria-label={t('user.mcp.confirmation', {defaultValue: 'Potvrzení odpojení AI klienta'})} className="app-surface mt-3 rounded-lg bg-rose-50 p-3 dark:bg-rose-950/30">
            <p className="text-sm text-datixo-negative">{t('user.mcp.revoke-warning', {clientName: grant.clientName, defaultValue: 'Klient {{clientName}} ztratí přístup ke všem projektům dostupným pod vaším účtem. Data už zkopírovaná externí službou se nesmažou.'})}</p>
            <div className="mt-3 flex flex-wrap gap-2">
                <Button ref={confirmButtonRef} variant="outline-red" size="small" disabled={revokingGrantId !== null} onClick={() => onConfirmRevoke(grant.grantId)}>{revokingGrantId === grant.grantId ? t('user.mcp.revoking', {defaultValue: 'Odpojuji…'}) : t('user.mcp.confirm-revoke', {defaultValue: 'Ano, odpojit všude'})}</Button>
                <Button variant="outline-primary" size="small" disabled={revokingGrantId !== null} onClick={onCancelRevoke}>{t('user.mcp.cancel', {defaultValue: 'Zrušit'})}</Button>
            </div>
        </div>}
    </article>)}</div>;
}

export default function AccountMcpConnections() {
    const {t} = useTranslation();
    const {context, getAuthorityConfiguration} = useContext(GlobalContext);
    const token = context.currentIdentity?.token ?? null;
    const isAdministrator = context.currentIdentity?.user?.isAdministrator === true;
    const apiRef = useRef({token: null, api: null});
    const identityTokenRef = useRef(token);
    const requestIdRef = useRef(0);
    const [grants, setGrants] = useState(null);
    const [resource, setResource] = useState(null);
    const [error, setError] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [confirmingGrantId, setConfirmingGrantId] = useState(null);
    const [revokingGrantId, setRevokingGrantId] = useState(null);
    identityTokenRef.current = token;
    if (apiRef.current.api === null || apiRef.current.token !== token) {
        apiRef.current = {token, api: new MCPOAuthApi(getAuthorityConfiguration())};
    }

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setGrants(null);
        setResource(null);
        setError(false);
        setConfirmingGrantId(null);
        setRevokingGrantId(null);
        const requestedToken = token;
        loadAccountMcpConnections(apiRef.current.api).then((value) => {
            if (requestIdRef.current === requestId && identityTokenRef.current === requestedToken) {
                setResource(value.resource);
                setGrants(value.grants);
            }
        }).catch(() => {
            if (requestIdRef.current === requestId && identityTokenRef.current === requestedToken) setError(true);
        });
        return () => { requestIdRef.current += 1; };
    }, [refreshKey, token]);

    const revoke = async (grantId) => {
        const requestId = ++requestIdRef.current;
        const requestedToken = token;
        setRevokingGrantId(grantId);
        setError(false);
        try {
            const value = await revokeAndReloadAccountMcpGrant(apiRef.current.api, grantId);
            if (requestIdRef.current === requestId && identityTokenRef.current === requestedToken) {
                setGrants(value);
                setConfirmingGrantId(null);
            }
        } catch {
            if (requestIdRef.current === requestId && identityTokenRef.current === requestedToken) setError(true);
        } finally {
            if (requestIdRef.current === requestId && identityTokenRef.current === requestedToken) setRevokingGrantId(null);
        }
    };

    return <section aria-label={t('user.mcp.title', {defaultValue: 'AI připojení'})}>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">{isAdministrator
            ? t('user.mcp.description-superadmin', {defaultValue: 'Jedno připojení zpřístupní AI klientovi všechny projekty, včetně projektů bez MCP v balíčku. Tento rozsah platí jen po dobu vašeho superadministrátorského oprávnění.'})
            : t('user.mcp.description', {defaultValue: 'Jedno připojení zpřístupní AI klientovi všechny vaše současné i budoucí projekty, které mají MCP aktivní v balíčku. Oprávnění se kontrolují při každém dotazu.'})}</p>
        {resource && <div className="mb-5">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('user.mcp.url', {defaultValue: 'Společná MCP URL'})}</div>
            <div className="app-surface mt-2 flex flex-col items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900 sm:flex-row">
                <span className="min-w-0 flex-1 break-all font-mono text-sm text-slate-800 dark:text-slate-200">{resource}</span>
                <Button variant="outline-primary" size="small" className="shrink-0" onClick={() => Promise.resolve().then(() => navigator.clipboard.writeText(resource)).then(() => toast.success(t('copy-to-clipboard-success'))).catch(() => toast.error(t('copy-to-clipboard-failed')))} aria-label={t('copy-to-clipboard')}>{t('copy', {defaultValue: 'Kopírovat'})}</Button>
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('user.mcp.url-help', {defaultValue: 'Tuto adresu vložte do AI klienta jednou. OAuth přihlášení propojí celý váš účet a budoucí způsobilé projekty se zpřístupní automaticky.'})}</p>
        </div>}
        {grants === null && !error && <p role="status" className="text-sm text-slate-500">{t('user.mcp.loading', {defaultValue: 'Načítám AI připojení…'})}</p>}
        {error && <div role="alert" className="app-surface rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-datixo-negative">
            <p>{t('user.mcp.error', {defaultValue: 'AI připojení se nepodařilo načíst nebo změnit.'})}</p>
            <Button variant="outline-red" size="small" className="mt-3" disabled={revokingGrantId !== null} onClick={() => setRefreshKey((value) => value + 1)}>{t('user.mcp.retry', {defaultValue: 'Zkusit znovu'})}</Button>
        </div>}
        {grants !== null && <AccountMcpGrantList grants={grants} confirmingGrantId={confirmingGrantId} revokingGrantId={revokingGrantId} onStartRevoke={setConfirmingGrantId} onCancelRevoke={() => setConfirmingGrantId(null)} onConfirmRevoke={revoke} t={t}/>}
    </section>;
}
